import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

import { licenceStore, type ScopedStore } from "./licenceScope";

/** Registre commun des saisies non enregistrées (audit de persistance du 03/10/2026, constats M4 et M6).
 *
 *  Chaque éditeur à bouton « Enregistrer » y déclare sa saisie tant qu'elle diffère de ce qui est enregistré
 *  (`useUnsavedDraft`) : éditeur d'ordre de bataille, paragraphe du dossier d'exercice, fiche d'incident DIREX,
 *  nouvel incident, notation 3A, résultats CENTAC, import RETEX, ordres reçus ; les Données initiales et la file de
 *  l'OPO, gardées sur le poste, y déclarent aussi ce qui n'est pas confirmé par le serveur. Le registre :
 *
 *  - fait demander confirmation au navigateur avant de fermer ou recharger l'onglet (`beforeunload`) ;
 *  - retient tout changement de vue, de dossier ou de langue (`leave`) derrière une seule confirmation commune,
 *    « Abandonner les modifications ? », celle de l'éditeur d'ordre de bataille : revenir à la saisie (choix par
 *    défaut), l'enregistrer si l'éditeur le peut, ou l'abandonner ;
 *  - recopie dans le navigateur, sous la licence en cours, la saisie des éditeurs qui en gardent une copie locale
 *    (passage en arrière-plan, fermeture, perte de la licence : `snapshotAll`).
 *
 *  Les saisies gardées sur le poste et rétablies d'elles-mêmes (Données initiales, OPO) ne retiennent pas la
 *  navigation (`navigation: false`) : changer de vue ne les perd pas. */

export interface DraftEntry {
  /** Ce qui n'est pas enregistré, en clair et traduit, avec une majuscule (« Ordre de bataille « GTIA Lynx » »). */
  label: string;
  /** Retient le changement de vue, de dossier ou de langue (vrai par défaut). */
  navigation: boolean;
  /** Fait demander confirmation avant de fermer ou recharger l'onglet (vrai par défaut). */
  unload: boolean;
  /** Enregistre la saisie ; rejetée si l'enregistrement échoue (l'utilisateur revient alors à la saisie). */
  save?: () => Promise<unknown>;
  /** Abandonne la saisie (l'éditeur revient à ce qui est enregistré). */
  discard?: () => void;
  /** Ramène à l'éditeur et désigne l'action attendue. */
  reveal?: () => void;
  /** Recopie la saisie dans le navigateur, tout de suite. */
  snapshot?: () => void;
}

/** Demande de sortie en attente de la confirmation commune. */
export interface LeaveRequest {
  id: number;
  /** Saisies qui retiennent la sortie, au moment de la demande. */
  labels: string[];
  /** Toutes peuvent s'enregistrer d'ici. */
  canSave: boolean;
  saving: boolean;
}

type Listener = () => void;

export class UnsavedDrafts {
  private entries = new Map<string, DraftEntry>();
  private request: LeaveRequest | null = null;
  private actions: (() => void)[] = [];
  private listeners = new Set<Listener>();
  private seq = 0;
  private bypassing = 0;
  private version = 0;
  /** Page sur le point d'être rechargée pour une autre licence : plus rien n'est retenu ni recopié. */
  private closed = false;

  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Demande de sortie en attente (confirmation commune ouverte), ou null. */
  getRequest = () => this.request;
  /** Change à chaque inscription ou retrait : de quoi relire `list()` dans un rendu. */
  getVersion = () => this.version;

  private emit() {
    this.version++;
    for (const listener of this.listeners) listener();
  }

  set(id: string, entry: DraftEntry): void {
    if (this.closed) return;
    this.entries.set(id, entry);
    this.emit();
  }

  delete(id: string): void {
    if (this.entries.delete(id)) this.emit();
  }

  has(id: string): boolean {
    return this.entries.has(id);
  }

  list(): DraftEntry[] {
    return [...this.entries.values()];
  }

  /** Saisies qui retiennent la navigation. */
  guarding(): DraftEntry[] {
    return this.list().filter((e) => e.navigation);
  }

  /** Une saisie doit-elle faire demander confirmation à la fermeture de l'onglet ? */
  blocksUnload(): boolean {
    return this.list().some((e) => e.unload);
  }

  /** Change de vue, de dossier ou de langue : tout de suite si rien ne le retient, sinon après la confirmation
   *  commune. Une demande déjà ouverte reçoit l'action à la suite (une seule question pour « ouvrir ce dossier,
   *  puis cette vue »). Vrai si l'action a été faite tout de suite. */
  leave(action: () => void): boolean {
    if (this.request) {
      this.actions.push(action);
      return false;
    }
    const guarding = this.bypassing ? [] : this.guarding();
    if (guarding.length === 0) {
      action();
      return true;
    }
    this.actions = [action];
    this.request = {
      id: ++this.seq,
      labels: guarding.map((e) => e.label),
      canSave: guarding.every((e) => Boolean(e.save)),
      saving: false,
    };
    this.emit();
    return false;
  }

  /** Fait `run` sans rien retenir (suite d'une confirmation déjà donnée par l'éditeur lui-même). */
  bypass(run: () => void): void {
    this.bypassing++;
    try {
      run();
    } finally {
      this.bypassing--;
    }
  }

  /** « Revenir à la saisie » : la sortie est oubliée, l'éditeur est montré. */
  stay(): void {
    if (!this.request) return;
    const first = this.guarding()[0];
    this.request = null;
    this.actions = [];
    this.emit();
    first?.reveal?.();
  }

  /** « Abandonner » : chaque saisie qui retenait la sortie est abandonnée, puis la sortie a lieu. */
  discard(): void {
    if (!this.request) return;
    for (const [id, entry] of [...this.entries]) {
      if (!entry.navigation) continue;
      entry.discard?.();
      this.entries.delete(id);
    }
    this.finish();
  }

  /** « Enregistrer » : la sortie n'a lieu qu'une fois tout enregistré ; un échec ramène à la saisie. */
  async save(): Promise<void> {
    const request = this.request;
    if (!request || !request.canSave || request.saving) return;
    const guarding = [...this.entries].filter(([, e]) => e.navigation);
    this.request = { ...request, saving: true };
    this.emit();
    try {
      await Promise.all(guarding.map(([, e]) => e.save?.()));
    } catch {
      if (this.request?.id === request.id) this.stay();
      return;
    }
    if (this.request?.id !== request.id) return;
    for (const [id] of guarding) this.entries.delete(id);
    this.finish();
  }

  private finish() {
    const actions = this.actions;
    this.request = null;
    this.actions = [];
    this.emit();
    this.bypass(() => {
      for (const action of actions) action();
    });
  }

  /** Rechargement pour une autre licence, ou poste libéré : les saisies de la licence quittée ne sont plus ni
   *  retenues ni recopiées (elles appartiennent à la licence quittée, effacée du navigateur). */
  close(): void {
    this.closed = true;
    this.entries.clear();
    this.request = null;
    this.actions = [];
    this.emit();
  }

  /** Recopie dans le navigateur toutes les saisies qui le peuvent (arrière-plan, fermeture, licence perdue). */
  snapshotAll(): void {
    if (this.closed) return;
    for (const entry of this.list()) {
      try {
        entry.snapshot?.();
      } catch {
        /* une copie impossible n'empêche pas les autres */
      }
    }
  }
}

/** Registre de l'application. */
export const unsavedDrafts = new UnsavedDrafts();

/** Sortie de l'application retenue par le registre (vue, dossier, langue). */
export const leaveWith = (action: () => void) => unsavedDrafts.leave(action);

let installed = false;

/** Protection à la fermeture de l'onglet et recopie des saisies quand la page passe en arrière-plan. */
export function installUnloadGuard(target: Window | undefined = typeof window === "undefined" ? undefined : window): void {
  if (installed || !target) return;
  installed = true;
  target.addEventListener("beforeunload", (e) => {
    unsavedDrafts.snapshotAll();
    if (!unsavedDrafts.blocksUnload()) return;
    e.preventDefault();
    // Navigateurs plus anciens : la confirmation ne s'affiche qu'avec une valeur de retour.
    e.returnValue = "";
  });
  target.addEventListener("pagehide", () => unsavedDrafts.snapshotAll());
  target.document?.addEventListener("visibilitychange", () => {
    if (target.document.visibilityState === "hidden") unsavedDrafts.snapshotAll();
  });
}

/** Demande de sortie en attente, pour la confirmation commune. */
export function useLeaveRequest(): LeaveRequest | null {
  return useSyncExternalStore(unsavedDrafts.subscribe, unsavedDrafts.getRequest, unsavedDrafts.getRequest);
}

/** Saisies inscrites (relu à chaque inscription ou retrait). */
export function useUnsavedList(): DraftEntry[] {
  useSyncExternalStore(unsavedDrafts.subscribe, unsavedDrafts.getVersion, unsavedDrafts.getVersion);
  return unsavedDrafts.list();
}

// --- Copies locales ------------------------------------------------------------------------------------------

/** Copie locale d'une saisie : la valeur, ce sur quoi elle porte (version, texte de départ) et sa date. */
export interface LocalCopy<T> {
  value: T;
  base: unknown;
  savedAt: number;
}

export function readLocalCopy<T>(key: string, store: ScopedStore = licenceStore): LocalCopy<T> | null {
  try {
    const copy = JSON.parse(store.get(key) ?? "null") as Partial<LocalCopy<T>> | null;
    if (!copy || copy.value === undefined) return null;
    return { value: copy.value, base: copy.base ?? null, savedAt: typeof copy.savedAt === "number" ? copy.savedAt : 0 };
  } catch {
    return null;
  }
}

export function writeLocalCopy(key: string, value: unknown, base: unknown, store: ScopedStore = licenceStore): void {
  try {
    store.set(key, JSON.stringify({ value, base, savedAt: Date.now() }));
  } catch {
    /* valeur impossible à recopier : la saisie reste à l'écran */
  }
}

export function clearLocalCopy(key: string, store: ScopedStore = licenceStore): void {
  store.remove(key);
}

/** Suivi de la copie locale d'un éditeur : la clé du dernier rendu et si la saisie y était modifiée. */
export interface CopyTrack {
  key: string | null;
  wasDirty: boolean;
}

/** Copie locale à effacer au passage d'un rendu au suivant, et suivi mis à jour.
 *
 *  - même clé, saisie redevenue enregistrée : la copie est effacée ;
 *  - clé disparue alors que la saisie était modifiée et ne l'est plus : l'éditeur l'a fermée lui-même (« Annuler »,
 *    enregistrement réussi — fiche d'incident DIREX dont la clé suit la fiche ouverte) : la copie est effacée, sans
 *    quoi elle reviendrait à la prochaine ouverture comme « Saisie retrouvée » ;
 *  - clé changée pour une autre (autre OPO, autre dossier) : la copie de l'ancienne clé est laissée, elle sera
 *    rétablie en y revenant ; la nouvelle clé part d'une saisie non modifiée. */
export function copyTransition(prev: CopyTrack, key: string | null, dirty: boolean): { next: CopyTrack; clear: string | null } {
  if (!key) {
    const clear = !dirty && prev.wasDirty && prev.key ? prev.key : null;
    return { next: { key: null, wasDirty: false }, clear };
  }
  const wasDirty = prev.key === key && prev.wasDirty;
  if (!dirty) return { next: { key, wasDirty: false }, clear: wasDirty ? key : null };
  return { next: { key, wasDirty: true }, clear: null };
}

/** Saisie d'un éditeur déclarée au registre tant qu'elle n'est pas enregistrée (`dirty`).
 *
 *  `copy` : copie locale de la saisie, rangée sous la licence (clé `generex-…-draft:…` de `LICENCE_KEYS`),
 *  écrite après une courte pause de frappe, aussitôt au passage en arrière-plan, à la fermeture et à la perte de
 *  la licence, et effacée quand la saisie est enregistrée ou abandonnée. L'éditeur la relit lui-même à
 *  l'ouverture (`readLocalCopy`) pour la rétablir avec un avis.
 *
 *  Rend `drop` : la saisie n'a plus d'objet (ordre de bataille supprimé…) ; sa copie est effacée et ne sera pas
 *  réécrite à la fermeture de l'éditeur. */
export function useUnsavedDraft(
  id: string | null,
  dirty: boolean,
  entry: Partial<Omit<DraftEntry, "label" | "snapshot">> & {
    label: string;
    copy?: { key: string; value: unknown; base?: unknown };
    /** Éditeur de vue (ordre de bataille, fiche d'incident) : fermé avec sa saisie (remontage), il en garde la copie.
     *  Sans `keep` (fenêtre), la fermeture est voulue — « Annuler » ou enregistrement réussi — : la copie est effacée. */
    keep?: boolean;
  },
): () => void {
  const ref = useRef(entry);
  ref.current = entry;
  const dropped = useRef(false);
  const copyKey = entry.copy?.key ?? null;
  const flush = useRef<() => void>(() => undefined);
  flush.current = () => {
    const copy = ref.current.copy;
    if (copy && dirty && !dropped.current) writeLocalCopy(copy.key, copy.value, copy.base ?? null);
  };

  // Copie locale : après une pause de frappe ; effacée quand la saisie redevient enregistrée (et seulement alors :
  // à l'ouverture, la copie d'une session précédente attend que l'éditeur la relise).
  const tracked = useRef<CopyTrack>({ key: null, wasDirty: false });
  const copyValue = entry.copy?.value;
  const copyBase = entry.copy?.base;
  useEffect(() => {
    const { next, clear } = copyTransition(tracked.current, copyKey, dirty);
    tracked.current = next;
    if (clear) clearLocalCopy(clear);
    if (!copyKey || !dirty) return;
    dropped.current = false;
    const timer = window.setTimeout(() => flush.current(), 250);
    return () => window.clearTimeout(timer);
  }, [copyKey, dirty, copyValue, copyBase]);

  useEffect(() => {
    if (!id || !dirty) return;
    const live = ref;
    unsavedDrafts.set(id, {
      get label() {
        return live.current.label;
      },
      get navigation() {
        return live.current.navigation ?? true;
      },
      get unload() {
        return live.current.unload ?? true;
      },
      // Enregistrable ou non selon l'état de l'éditeur au moment de la question (erreur, conflit en attente).
      get save() {
        const save = live.current.save;
        // Enregistrée d'ici : la copie n'a plus d'objet, même si l'éditeur se ferme avant d'afficher l'état enregistré.
        return save
          ? () =>
              save().then((result) => {
                dropped.current = true;
                const key = live.current.copy?.key;
                if (key) clearLocalCopy(key);
                return result;
              })
          : undefined;
      },
      discard: () => {
        dropped.current = true;
        const key = live.current.copy?.key;
        if (key) clearLocalCopy(key);
        live.current.discard?.();
      },
      reveal: () => live.current.reveal?.(),
      snapshot: () => flush.current(),
    });
    return () => {
      unsavedDrafts.delete(id);
      // Éditeur de vue fermé avec sa saisie (remontage) : la copie est écrite tout de suite. Fenêtre fermée : sa
      // copie n'a plus d'objet.
      if (live.current.keep) flush.current();
      else if (live.current.copy?.key) clearLocalCopy(live.current.copy.key);
    };
  }, [id, dirty]);

  return useCallback(() => {
    dropped.current = true;
    const key = ref.current.copy?.key;
    if (key) clearLocalCopy(key);
    if (id) unsavedDrafts.delete(id);
  }, [id]);
}
